import { useFlags } from "launchdarkly-react-client-sdk";
import { useCallback, useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useForm } from "react-hook-form";
import ReactMarkdown from "react-markdown";

import AudioFileIcon from "@mui/icons-material/AudioFile";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ContentPasteGoIcon from "@mui/icons-material/ContentPasteGo";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { SxProps, Theme } from "@mui/material/styles";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { neutral } from "app/themes/colors";
import CheckboxFormField from "components/checkbox-form-field";
import Dialog from "components/dialog";
import { AMBIENT_SCRIBE_PAUSED_FEATURE } from "constants/feature-flags";
import { AMBIENT_SCRIBE_FEATURE_KEY } from "constants/paywall";
import TrialStatusBanner from "features/medical-scribe/components/trial-status-banner.component";
import {
  AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED,
  AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING,
  AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING,
  getScribeModalPopoverZIndex,
  getScribeModalZIndex
} from "features/medical-scribe/medical-scribe-constants";
import ViewScribeDetails from "features/medical-scribe/medical-scribes-section/scribes-list/scribe-details-dialog/view-scribe-details";
import { getMedicalScribeText } from "features/medical-scribe/medical-scribes-section/scribes-list/utils";
import { useMedicalScribe } from "features/medical-scribe/use-medical-scribe.hook";
import type { AmbientScribe } from "services/types/ambient-scribe.type";
import { isLikelyHtml } from "utils/markdown.utils";

import useCopyHtmlContentToClipboard from "../hooks/use-copy-html-content-to-clipboard.hook";
import {
  showInsertScribe,
  showInsertScribeForChartNoteId
} from "../patient-chart-notes.slice";
import SelectMedicalScribeField from "./select-medical-scribe-field.component";
import { getScribeInsertAction } from "./utils";

type InsertScribeValues = {
  needReplace: boolean;
  isCollapsed: boolean;
  tabView: string;
};

const DEFAULT_VALUES: InsertScribeValues = {
  needReplace: false,
  isCollapsed: false,
  tabView: ""
};

// Static styles - extracted to avoid recreation on every render
const STATIC_STYLES = {
  mainContainer: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden"
  } as const,
  contentWrapper: {
    flex: 1,
    overflow: "hidden",
    pb: 2
  } as const,
  emptyStateContainer: {
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center"
  } as const,
  emptyStateStack: {
    opacity: 0.9,
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
    borderRadius: "6px",
    border: "solid 1px #EFF0EF",
    py: 4
  } as const,
  audioIcon: {
    fontSize: 48
  } as const,
  emptyStateText: {
    mt: 1
  } as const,
  collapseButton: {
    textTransform: "uppercase"
  } as const
};

type InsertScribeDialogProps = {
  patientId: number;
};

export default function InsertScribeDialog({
  patientId
}: InsertScribeDialogProps) {
  const theme = useTheme();
  const { zIndex, shadows } = theme;
  const dispatch = useAppDispatch();
  const flags = useFlags();
  const isPausedFeatureEnabled = flags[AMBIENT_SCRIBE_PAUSED_FEATURE] ?? false;

  const { editorRef, insertScribeSelectionBookmarkRef } = useMedicalScribe();

  const [scribeDetailsMode, setScribeDetailsMode] = useState<"view" | "edit">(
    "view"
  );
  const [editBottomTab, setEditBottomTab] = useState<"sessions" | "context">(
    "sessions"
  );

  const insertScribeState = useAppSelector(showInsertScribeForChartNoteId);
  useEffect(() => {
    if (insertScribeState.show) {
      setScribeDetailsMode("view");
      setEditBottomTab("sessions");
    }
  }, [insertScribeState.show]);

  // Responsive breakpoints
  const isTablet = useMediaQuery(theme.breakpoints.between("tablet", "mobile")); // Between 768px and 1320px
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));

  const formMethods = useForm<InsertScribeValues>({
    mode: "onChange",
    defaultValues: DEFAULT_VALUES
  });

  const {
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { isValid }
  } = formMethods;

  const [selectedScribe, setSelectedScribe] = useState<AmbientScribe | null>(
    null
  );

  // Latest data from the detail query poll inside ViewScribeDetails.
  // Kept separate from selectedScribe to avoid triggering SelectMedicalScribeField's
  // sync-back effect, which would revert it to the stale list snapshot.
  const [latestRemoteScribe, setLatestRemoteScribe] =
    useState<AmbientScribe | null>(null);

  useEffect(() => {
    setLatestRemoteScribe(null);
  }, [selectedScribe?.id]);

  const [isCollapsed, tabView] = watch(["isCollapsed", "tabView"]);

  const [search, setSearch] = useState("");

  // const [dialogText, setDialogText] = useState<string>("");
  // const [showAsDialog, setShowAsDialog] = useState<boolean>(false);

  const handleClose = useCallback(
    (event?: object, reason?: string) => {
      if (reason === "backdropClick") {
        return;
      }
      insertScribeSelectionBookmarkRef.current = null;
      reset(DEFAULT_VALUES);
      dispatch(showInsertScribe({ show: false }));
    },
    [dispatch, insertScribeSelectionBookmarkRef, reset]
  );

  // Prefer polled detail data over the stale list snapshot, but only when the
  // IDs match. Number() normalizes the id since AmbientScribe.id is number|string.
  const effectiveScribe =
    latestRemoteScribe != null &&
    Number(latestRemoteScribe.id) === Number(selectedScribe?.id)
      ? latestRemoteScribe
      : selectedScribe;

  const getText = useCallback(() => {
    const scribe = effectiveScribe;
    let text: string =
      tabView === "summary"
        ? String(scribe?.summary ?? "")
        : tabView === "soap"
          ? String(scribe?.soap_notes ?? "")
          : getMedicalScribeText(scribe);
    // showAsDialog
    //   ? dialogText.replaceAll("\n", "<br />")
    //   :
    if (
      text &&
      (tabView === "summary" || tabView === "soap") &&
      !isLikelyHtml(text)
    ) {
      text = renderToStaticMarkup(<ReactMarkdown>{text}</ReactMarkdown>);
    }
    return text;
  }, [effectiveScribe, tabView]);

  const handleCopyText = useCopyHtmlContentToClipboard();

  const handleCopyTextAndClose = useCallback(async () => {
    const text = getText();
    await handleCopyText(text, true);
    handleClose();
  }, [getText, handleCopyText, handleClose]);

  const onSubmit = useCallback(
    async (data: InsertScribeValues) => {
      const { needReplace } = data;
      const text = getText();

      const action = getScribeInsertAction({
        hasEditor: Boolean(editorRef?.current),
        needReplace,
        text
      });

      if (action === "copy") {
        await handleCopyText(text, true);
      } else if (action === "replace") {
        insertScribeSelectionBookmarkRef.current = null;
        editorRef?.current?.setContent(text);
      } else if (action === "insert") {
        const editor = editorRef?.current;
        if (editor) {
          const bookmark = insertScribeSelectionBookmarkRef.current;
          if (bookmark) {
            try {
              editor.selection.moveToBookmark(bookmark);
            } catch {
              // bookmark restore failed; insert at current caret
            }
            insertScribeSelectionBookmarkRef.current = null;
          }
          editor.focus();
          editor.insertContent(text);
        }
      }
      // action === "noop": empty replace — leave the chart note untouched.

      handleClose();
    },
    [getText, editorRef, insertScribeSelectionBookmarkRef, handleClose, handleCopyText]
  );

  const handleSelect = useCallback(
    (value: AmbientScribe | null) => {
      setSelectedScribe(value);
      if (isPausedFeatureEnabled) {
        setScribeDetailsMode("view");
        setEditBottomTab("sessions");
      }
    },
    [isPausedFeatureEnabled]
  );

  const handleEnterEdit = useCallback((bottomTab: "sessions" | "context") => {
    setEditBottomTab(bottomTab);
    setScribeDetailsMode("edit");
  }, []);

  const handleOpenEditScribe = useCallback((scribe: AmbientScribe) => {
    setSelectedScribe(scribe);
    setEditBottomTab("sessions");
    setScribeDetailsMode("edit");
  }, []);

  const handleScribeUpdated = useCallback((updated: AmbientScribe) => {
    setLatestRemoteScribe(updated);
  }, []);

  const handleTabSelected = useCallback(
    (value: string) => {
      setValue("tabView", value);
    },
    [setValue]
  );

  const hasSummary = Boolean(tabView === "summary" && effectiveScribe?.summary);
  const hasSoapNotes = Boolean(
    tabView === "soap" && effectiveScribe?.soap_notes
  );
  const hasTranscript = Boolean(
    tabView === "transcript" && effectiveScribe?.transcription
  );
  // Responsive sizing - memoized for performance
  const minWidth = useMemo(() => {
    if (isMobile) return 320;
    if (isTablet) return isCollapsed ? 600 : 1000;
    return isCollapsed ? 900 : 1538;
  }, [isMobile, isTablet, isCollapsed]);

  const height = useMemo(() => {
    if (isMobile) return isCollapsed ? "38vh" : "90vh";
    if (isTablet) return isCollapsed ? "35.5vh" : "85vh";
    return isCollapsed ? "480px" : "740px";
  }, [isMobile, isTablet, isCollapsed]);

  const isActionDisabled =
    !isValid ||
    !selectedScribe ||
    !(hasSummary || hasSoapNotes || hasTranscript);

  const showInsertActions =
    !isPausedFeatureEnabled || scribeDetailsMode === "view";

  // Memoize button text to avoid recalculation
  const buttonText = useMemo(() => {
    const prefix = editorRef?.current ? "Insert" : "Copy";
    if (tabView === "summary") return `${prefix} Summary`;
    if (tabView === "soap") return `${prefix} Scribe`;
    // if (showAsDialog) return `${prefix} Dialog`;
    return `${prefix} Transcript`;
  }, [tabView, editorRef]);

  // Memoize collapse toggle handler
  const handleToggleCollapse = useCallback(() => {
    setValue("isCollapsed", !isCollapsed);
  }, [isCollapsed, setValue]);

  // Memoize form submit handler
  const handleFormSubmit = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  // Memoize dynamic styles
  const dialogContentSx = useMemo(
    () => ({
      display: "flex",
      p: isMobile ? 2 : 3,
      pb: 0,
      flexDirection: "column",
      boxSizing: "border-box",
      height,
      overflow: "hidden",
      border: `solid 1px ${neutral[500]}`,
      boxShadow: shadows[3],
      background: neutral[50]
    }),
    [isMobile, height, shadows]
  );

  const dialogTitleSx = useMemo(
    () => ({
      border: `solid 1px ${neutral[400]}`,
      boxShadow: shadows[2]
    }),
    [shadows]
  );

  const dialogSx = useMemo(
    () => ({
      zIndex: getScribeModalZIndex(zIndex.snackbar),
      pointerEvents: isCollapsed ? "none" : "auto"
    }),
    [zIndex, isCollapsed]
  );

  const tooltipPopperProps = useMemo(
    () => ({ style: { zIndex: getScribeModalPopoverZIndex(zIndex.snackbar) } }),
    [zIndex.snackbar]
  );

  const stackSx = useMemo(
    () => ({
      boxSizing: "border-box",
      height: "100%",
      gap: 2,
      width: "100%"
    }),
    []
  );

  const leftPanelSx = useMemo(
    () => ({
      height: isTablet ? "40vh" : "100%",
      overflowY: "auto",
      overflowX: "hidden",
      minWidth: isTablet ? "100%" : "633px",
      maxWidth: isTablet ? "100%" : "none",
      borderRight: isTablet ? "none" : `solid 1px ${neutral[500]}`,
      borderBottom: isTablet ? `solid 1px ${neutral[500]}` : "none",
      flex: "0 0 auto",
      pb: isTablet ? 2 : 0
    }),
    [isTablet]
  );

  const isEditScribePanel =
    isPausedFeatureEnabled &&
    scribeDetailsMode === "edit" &&
    Boolean(selectedScribe);

  const rightPanelSx = useMemo((): SxProps<Theme> => {
    const base: SxProps<Theme> = {
      flex: isTablet ? "1 1 auto" : 1,
      minWidth: isTablet ? "100%" : "300px",
      minHeight: 0,
      overflowX: "hidden",
      mt: isTablet ? 0 : -1,
      display: "flex",
      flexDirection: "column",
      overflowY: isEditScribePanel ? "hidden" : "auto"
    };
    if (isTablet) {
      return {
        ...base,
        maxHeight: isActionDisabled ? "45vh" : "38vh"
      };
    }
    if (isEditScribePanel) {
      return { ...base, height: "100%" };
    }
    return { ...base, maxHeight: "680px" };
  }, [isTablet, isActionDisabled, isEditScribePanel]);

  const dialogActionsSx = useMemo(
    () => ({
      background: neutral[50],
      borderTop: `solid 1px ${neutral[200]}`,
      px: isMobile ? 1 : 3,
      py: isTablet ? 0 : 2,
      pb: 1,
      pr: 0,
      mt: "auto",
      flexDirection: isMobile ? "column" : "row",
      alignItems: isMobile ? "stretch" : "center",
      gap: isMobile ? 1 : 0
    }),
    [isMobile, isTablet]
  );

  const formControlLabelSx = useMemo(
    () => ({
      mr: isMobile ? 0 : 2,
      mb: isMobile ? -1 : 0,
      alignSelf: isMobile ? "flex-start" : "center"
    }),
    [isMobile]
  );

  const submitButtonSx = useMemo(
    () => ({
      ml: isMobile ? 0 : 2,
      mr: isMobile ? 0 : 2,
      width: isMobile ? "calc(100% - 16px)" : "auto",
      minHeight: isTablet ? "44px" : "auto"
    }),
    [isMobile, isTablet]
  );

  return (
    <Dialog
      title={isPausedFeatureEnabled ? "Scribes" : "Select a Medical Scribe"}
      minWidth={minWidth}
      onClose={handleClose}
      open
      hideBackdrop={isCollapsed}
      disableEnforceFocus={isCollapsed}
      disableScrollLock={isCollapsed}
      dialogContentProps={{ sx: dialogContentSx }}
      dialogTitleSx={dialogTitleSx}
      isDraggable
      sx={dialogSx}
      extraTitleButton={
        <Button
          size="small"
          variant="contained"
          onClick={handleToggleCollapse}
          onTouchStart={handleToggleCollapse}
          disabled={!selectedScribe}
          sx={STATIC_STYLES.collapseButton}
        >
          {isCollapsed ? (
            <>
              <UnfoldMoreIcon /> Expand
            </>
          ) : (
            <>
              <UnfoldLessIcon /> Collapse
            </>
          )}
        </Button>
      }
    >
      <TrialStatusBanner featureKey={AMBIENT_SCRIBE_FEATURE_KEY} />
      <Box sx={STATIC_STYLES.mainContainer}>
        <Box sx={STATIC_STYLES.contentWrapper}>
          <Stack direction={isTablet ? "column" : "row"} sx={stackSx}>
            {!isCollapsed && (
              <Box sx={leftPanelSx}>
                <SelectMedicalScribeField
                  patientId={patientId}
                  search={search}
                  setSearch={setSearch}
                  selected={selectedScribe}
                  onSelect={handleSelect}
                  onSelectedScribeSync={setSelectedScribe}
                  onEdit={
                    isPausedFeatureEnabled ? handleOpenEditScribe : undefined
                  }
                  extraStatuses={
                    isPausedFeatureEnabled
                      ? [
                          AMBIENT_SCRIBE_BACKEND_STATUS_PAUSED,
                          AMBIENT_SCRIBE_BACKEND_STATUS_PROCESSING,
                          AMBIENT_SCRIBE_BACKEND_STATUS_RECORDING
                        ]
                      : undefined
                  }
                />
              </Box>
            )}
            <Box sx={rightPanelSx}>
              {!selectedScribe ? (
                <Box sx={STATIC_STYLES.emptyStateContainer}>
                  <Stack sx={STATIC_STYLES.emptyStateStack}>
                    <AudioFileIcon sx={STATIC_STYLES.audioIcon} />
                    <Typography
                      variant="body3"
                      sx={STATIC_STYLES.emptyStateText}
                    >
                      Please, select a Medical Scribe Record
                    </Typography>
                  </Stack>
                </Box>
              ) : (
                <ViewScribeDetails
                  key={selectedScribe?.id}
                  remoteMedicalScribeId={Number(selectedScribe?.id)}
                  detailsMode={
                    isPausedFeatureEnabled ? scribeDetailsMode : undefined
                  }
                  onDetailsModeChange={
                    isPausedFeatureEnabled ? setScribeDetailsMode : undefined
                  }
                  editBottomTab={
                    isPausedFeatureEnabled ? editBottomTab : undefined
                  }
                  onEditBottomTabChange={
                    isPausedFeatureEnabled ? setEditBottomTab : undefined
                  }
                  onEnterEdit={
                    isPausedFeatureEnabled ? handleEnterEdit : undefined
                  }
                  onEditClose={handleClose}
                  onEditFinalized={() => {
                    setScribeDetailsMode("view");
                    setEditBottomTab("sessions");
                  }}
                  onTabSelected={handleTabSelected}
                  onScribeUpdated={handleScribeUpdated}
                  hideHeader={isCollapsed}
                />
              )}
            </Box>
          </Stack>
        </Box>
        {showInsertActions &&
          (!isTablet || !isActionDisabled) &&
          selectedScribe && (
            <DialogActions sx={dialogActionsSx}>
              {editorRef?.current && (
                <FormControlLabel
                  label="Replace existing content"
                  sx={formControlLabelSx}
                  control={
                    <CheckboxFormField
                      name="needReplace"
                      control={control}
                      testId="need-replace"
                    />
                  }
                />
              )}
              <Button
                disabled={isActionDisabled}
                variant="contained"
                sx={submitButtonSx}
                onClick={handleFormSubmit}
                startIcon={
                  editorRef?.current ? (
                    <ContentPasteGoIcon />
                  ) : (
                    <ContentCopyIcon />
                  )
                }
              >
                {buttonText}
              </Button>
              {editorRef?.current && (
                <Tooltip
                  title="Copy text and close"
                  arrow
                  disableInteractive
                  PopperProps={tooltipPopperProps}
                >
                  <IconButton
                    onClick={handleCopyTextAndClose}
                    color="primary"
                    sx={{ border: "1px solid", borderColor: "divider" }}
                  >
                    <ContentCopyIcon />
                  </IconButton>
                </Tooltip>
              )}
            </DialogActions>
          )}
      </Box>
    </Dialog>
  );
}
